import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import clsx from 'clsx';
import { Modal } from '../../../../../components/ui/Modal';
import { Button } from '../../../../../components/ui/Button';
import { Field, inputClass } from '../../../../../components/ui/Field';
import { FilterableSelect } from '../../../../../components/ui/FilterableSelect';
import { adjustStock, fetchAdjustBuckets } from '../../../../../features/stock/stockThunks';
import { clearStockAdjust } from '../../../../../features/stock/stockSlice';
import { bucketKey } from '../../../../../features/stock/stockTransferUtils';
import {
  ADJUST_DIRECTION,
  adjustBucketLabel,
  adjustDoneMessage,
  adjustProblem,
  buildAdjustPayload,
} from '../../../../../features/stock/stockAdjustUtils';
import { fetchSlotOptions } from '../../../../../features/slots/slotsThunks';
import { slotOptionLabel } from '../../../../../features/slots/slotsUtils';
import { formatQuantity } from '../../../../../utils/inventoryLabels';

const EMPTY_FORM = { direction: ADJUST_DIRECTION.ADD, slotId: '', fromKey: '', quantity: '', notes: '' };

const DIRECTIONS = [
  { value: ADJUST_DIRECTION.ADD, label: 'Añadir' },
  { value: ADJUST_DIRECTION.REMOVE, label: 'Quitar' },
];

/**
 * Adds or removes units of one product by hand, with a reason.
 *
 * The inventory is fed by receipts and drained by orders; this is the door for
 * everything else — a count that did not match, a broken unit, a box found on
 * the wrong shelf. It lands in the kardex as an adjustment with the reason on
 * it, so the number can be explained later.
 *
 * @param {object} props
 * @param {boolean} props.isOpen
 * @param {() => void} props.onClose
 * @param {object} props.warehouse - The warehouse in use; never "todas".
 * @param {object|null} props.row - The row it was opened from: the product, and
 *   with the detail also the location and the lot.
 * @param {(message: string) => void} props.onDone - Told what happened, in a sentence.
 */
export const AdjustStockModal = ({ isOpen, onClose, warehouse, row, onDone }) => {
  const dispatch = useDispatch();
  const { buckets, loadingBuckets, saving } = useSelector((state) => state.stock.adjust);
  const { options: slots, loadingOptions } = useSelector((state) => state.slots);

  const [form, setForm] = useState(EMPTY_FORM);
  const [saveError, setSaveError] = useState(null);

  const warehouseId = warehouse?.id;
  const productId = row?.product ?? null;

  // Every opening starts from the row that asked for it, and closing forgets
  // the places: the next product must not open on this one's.
  useEffect(() => {
    if (!isOpen) {
      dispatch(clearStockAdjust());
      return;
    }
    setSaveError(null);
    setForm({ ...EMPTY_FORM, slotId: row?.slot ?? '' });
    if (warehouseId) {
      dispatch(fetchSlotOptions(warehouseId));
      if (productId) dispatch(fetchAdjustBuckets({ productId, warehouseId }));
    }
  }, [isOpen, row, productId, warehouseId, dispatch]);

  // Opened from a row of the detail: that location, and that lot, is where the
  // units leave from.
  useEffect(() => {
    if (!row?.slot || buckets.length === 0) return;
    const match = buckets.find(
      (bucket) => bucket.slot === row.slot && (bucket.lot || '') === (row.lot || '')
    );
    if (!match) return;
    setForm((prev) => (prev.fromKey ? prev : { ...prev, fromKey: bucketKey(match) }));
  }, [row, buckets]);

  const set = (patch) => setForm((prev) => ({ ...prev, ...patch }));

  const removing = form.direction === ADJUST_DIRECTION.REMOVE;
  const from = buckets.find((bucket) => bucketKey(bucket) === form.fromKey) || null;
  const slot = slots.find((option) => option.id === form.slotId) || null;

  const draft = { ...form, from };
  const problem = adjustProblem(draft);

  const fromHint = (() => {
    if (loadingBuckets) return 'Buscando dónde hay...';
    return buckets.length > 0
      ? 'Solo se listan los lugares con stock disponible.'
      : 'Este producto no tiene stock disponible en esta bodega.';
  })();

  const handleSubmit = async () => {
    setSaveError(null);
    const action = await dispatch(
      adjustStock(buildAdjustPayload({ ...draft, productId, warehouseId, lot: row?.lot }))
    );

    if (action.meta.requestStatus !== 'fulfilled') {
      setSaveError(action.payload);
      return;
    }

    onDone(
      adjustDoneMessage({
        direction: form.direction,
        sku: row?.product_sku || 'el producto',
        placeName: removing ? from?.slot_name || 'Sin ubicar' : slot?.name || 'su ubicación',
        quantity: form.quantity,
      })
    );
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Ajustar inventario" testId="adjust-modal">
      <p className="text-sm text-text-secondary text-center -mt-4 mb-6" data-testid="adjust-product">
        <span className="font-mono font-semibold text-ninja-dark dark:text-white">{row?.product_sku}</span>
        {row?.product_name && ` · ${row.product_name}`}
        {warehouse?.name && ` · ${warehouse.name}`}
      </p>

      <div className="space-y-4">
        {saveError && (
          <div
            data-testid="adjust-error"
            className="bg-red-50 dark:bg-red-900/20 text-danger p-3 rounded-xl border border-red-200 dark:border-red-900 text-sm"
          >
            {saveError}
          </div>
        )}

        {/* One act with two directions: the form below follows the one picked. */}
        <div
          role="group"
          aria-label="Qué hacer"
          className="flex rounded-xl bg-ninja-gray/50 dark:bg-[#111] p-1 gap-1"
          data-testid="adjust-direction"
        >
          {DIRECTIONS.map((option) => {
            const active = form.direction === option.value;
            return (
              <button
                key={option.value}
                type="button"
                aria-pressed={active}
                data-testid={`adjust-direction-${option.value}`}
                disabled={saving}
                onClick={() => set({ direction: option.value, quantity: '' })}
                className={clsx(
                  'flex-1 px-4 py-2 text-sm font-semibold rounded-lg transition-colors',
                  active
                    ? 'bg-white dark:bg-ninja-dark text-ninja-dark dark:text-white shadow-sm'
                    : 'text-text-secondary hover:text-ninja-dark dark:hover:text-white'
                )}
              >
                {option.label}
              </button>
            );
          })}
        </div>

        {removing ? (
          <div>
            <FilterableSelect
              variant="form"
              testId="adjust-from"
              label="Desde"
              value={form.fromKey}
              onChange={(fromKey) => set({ fromKey, quantity: '' })}
              options={buckets.map((bucket) => ({ value: bucketKey(bucket), label: adjustBucketLabel(bucket) }))}
              placeholder="Seleccionar..."
              searchPlaceholder="Buscar ubicación o lote..."
              disabled={loadingBuckets || saving}
            />
            <p data-testid="adjust-from-hint" className="text-text-muted text-xs mt-1">
              {fromHint}
            </p>
          </div>
        ) : (
          <div>
            <FilterableSelect
              variant="form"
              testId="adjust-slot"
              label="Ubicación"
              value={form.slotId}
              onChange={(slotId) => set({ slotId })}
              options={slots.map((option) => ({ value: option.id, label: slotOptionLabel(option) }))}
              placeholder={loadingOptions ? 'Cargando ubicaciones...' : 'Seleccionar...'}
              searchPlaceholder="Buscar por nombre o código..."
              disabled={saving}
            />
            <p className="text-text-muted text-xs mt-1">
              Las unidades quedan disponibles en esa ubicación
              {row?.lot ? `, lote ${row.lot}.` : '.'}
            </p>
          </div>
        )}

        <Field
          label="Cantidad"
          htmlFor="adjust-quantity"
          hint={
            removing && from
              ? `Disponible en ${from.slot_name || 'Sin ubicar'}: ${formatQuantity(from.quantity)} ${from.base_uom}`
              : undefined
          }
        >
          <input
            id="adjust-quantity"
            type="number"
            min="0"
            step="any"
            data-testid="adjust-quantity"
            value={form.quantity}
            onChange={(event) => set({ quantity: event.target.value })}
            disabled={(removing && !from) || saving}
            className={inputClass}
          />
        </Field>

        <Field
          label="Motivo"
          htmlFor="adjust-notes"
          hint="Obligatorio: queda escrito en el kardex (merma, rotura, descuadre de conteo...)."
        >
          <textarea
            id="adjust-notes"
            rows={2}
            data-testid="adjust-notes"
            value={form.notes}
            onChange={(event) => set({ notes: event.target.value })}
            disabled={saving}
            className={clsx(inputClass, 'resize-none')}
          />
        </Field>
      </div>

      <div className="flex justify-end gap-3 mt-8">
        <Button variant="ghost" onClick={onClose} disabled={saving}>
          Cancelar
        </Button>
        {/* Disabled, it says what is missing: a dead button with no reason is
            a form somebody gives up on. */}
        <Button
          variant="primary"
          loading={saving}
          loadingLabel="Guardando..."
          disabled={Boolean(problem)}
          onClick={handleSubmit}
          data-testid="adjust-submit"
        >
          {problem || (removing ? 'Quitar' : 'Añadir')}
        </Button>
      </div>
    </Modal>
  );
};
